import { debugLog, errorLog } from './utils/logger';

/**
 * Price calculator
 * Fetches current prices and applies slippage for market orders
 */

import type { AssetMetadataManager } from './asset-manager';

/**
 * Handles price fetching and calculations for orders
 */
export class PriceCalculator {
  private infoApiUrl: string;
  private priceCache: Map<string, { price: string; timestamp: number }> = new Map();
  private cacheTTL: number = 10 * 1000; // 10 seconds for price cache
  private refreshInterval: NodeJS.Timeout | null = null;

  constructor(_assetManager: AssetMetadataManager, isTestnet: boolean = false) {
    // Asset manager is passed for potential future use (e.g., tick size validation)
    this.infoApiUrl = isTestnet
      ? 'https://api.hyperliquid-testnet.xyz/info'
      : 'https://api.hyperliquid.xyz/info';
  }

  /**
   * Get current mark price for a symbol
   */
  async getCurrentPrice(symbol: string): Promise<string> {
    // Check cache
    const cached = this.priceCache.get(symbol);
    const now = Date.now();

    if (cached && now - cached.timestamp < this.cacheTTL) {
      debugLog(`[PriceCalculator] Using cached price for ${symbol}:`, cached.price);
      return cached.price;
    }

    // Fetch all prices (more efficient than per-symbol)
    debugLog(`[PriceCalculator] Fetching fresh prices for ${symbol}`);
    const allPrices = await this.fetchAllPrices();

    debugLog(`[PriceCalculator] All prices type:`, typeof allPrices);
    debugLog(`[PriceCalculator] All prices keys sample:`, Object.keys(allPrices || {}).slice(0, 5));

    if (!allPrices) {
      throw new Error('Failed to fetch prices from Hyperliquid API');
    }

    const price = allPrices[symbol];

    if (!price) {
      throw new Error(`Price not found for symbol "${symbol}". Available symbols: ${Object.keys(allPrices).slice(0, 10).join(', ')}`);
    }

    debugLog(`[PriceCalculator] Found price for ${symbol}:`, price);

    // Cache the price
    this.priceCache.set(symbol, { price, timestamp: now });

    return price;
  }

  /**
   * Calculate market order price with slippage
   * Returns UNROUNDED price with full precision - caller must apply proper tick size rounding
   *
   * @param symbol - Asset symbol
   * @param side - 'buy' or 'sell'
   * @param slippage - Slippage percentage (e.g., 0.05 for 5%)
   * @returns Price string with slippage applied (unrounded - use roundToTickSize after)
   */
  async calculateMarketPrice(
    symbol: string,
    side: 'buy' | 'sell',
    slippage: number = 0.05
  ): Promise<string> {
    const currentPrice = await this.getCurrentPrice(symbol);
    const priceNum = parseFloat(currentPrice);

    if (isNaN(priceNum) || priceNum <= 0) {
      throw new Error(`Invalid price for ${symbol}: ${currentPrice}`);
    }

    // Apply slippage
    // For buys: increase price to ensure execution (pay more)
    // For sells: decrease price to ensure execution (receive less)
    const slippageFactor = side === 'buy' ? 1 + slippage : 1 - slippage;
    const priceWithSlippage = priceNum * slippageFactor;

    // Return unrounded price with full precision
    // Caller will apply proper Hyperliquid tick size rules via roundToTickSize()
    return priceWithSlippage.toString();
  }

  /**
   * Fetch all mark prices from Hyperliquid
   * Caches ALL prices for 10 seconds to optimize subsequent calls
   */
  private async fetchAllPrices(): Promise<Record<string, string>> {
    try {
      const response = await fetch(this.infoApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'allMids' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch prices: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle different response formats
      let allPrices: Record<string, string>;
      if (data && typeof data === 'object') {
        // If response is directly the mids object (not wrapped)
        if (!data.mids && typeof data === 'object') {
          allPrices = data as Record<string, string>;
        } else if (data.mids && typeof data.mids === 'object') {
          // If response has mids property
          allPrices = data.mids;
        } else {
          errorLog('[PriceCalculator] Unexpected response format:', data);
          throw new Error('Unexpected response format from allMids API');
        }
      } else {
        throw new Error('Invalid response from allMids API');
      }

      // Cache ALL prices at once for better performance
      const now = Date.now();
      let cachedCount = 0;
      for (const [symbol, price] of Object.entries(allPrices)) {
        this.priceCache.set(symbol, { price, timestamp: now });
        cachedCount++;
      }

      debugLog(`[PriceCalculator] Cached ${cachedCount} prices (TTL: 10s)`);
      return allPrices;
    } catch (error) {
      errorLog('[PriceCalculator] Failed to fetch prices:', error);
      throw new Error(
        `Unable to fetch current prices: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Clear price cache (useful for forcing refresh)
   */
  clearCache(): void {
    this.priceCache.clear();
  }

  /**
   * Round price to valid Hyperliquid precision
   *
   * Rules:
   * - Maximum 5 significant figures
   * - Maximum (MAX_DECIMALS - szDecimals) decimal places
   * - For perps: MAX_DECIMALS = 6
   * - Remove trailing zeros
   *
   * @param price - Price to round
   * @param szDecimals - Size decimals for the asset (from metadata)
   * @returns Price rounded to valid precision
   */
  roundToTickSize(price: number | string, szDecimals: number = 4): string {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;

    if (isNaN(priceNum) || priceNum <= 0) {
      throw new Error(`Invalid price: ${price}`);
    }

    const MAX_DECIMALS = 6; // For perpetuals
    const maxDecimalPlaces = MAX_DECIMALS - szDecimals;

    // Count significant figures
    const priceStr = priceNum.toString();
    const [intPart, decPart] = priceStr.split('.');

    // If integer, always valid regardless of sig figs
    if (!decPart) {
      debugLog(`[PriceCalculator] Integer price ${priceNum} - always valid`);
      return priceNum.toString();
    }

    // Apply max 5 significant figures rule
    const sigFigs = (intPart.replace(/^0+/, '') + decPart.replace(/0+$/, '')).length;
    if (sigFigs > 5) {
      // Round to 5 significant figures
      const magnitude = Math.floor(Math.log10(Math.abs(priceNum)));
      const factor = Math.pow(10, magnitude - 4);
      const rounded5sig = Math.round(priceNum / factor) * factor;
      debugLog(`[PriceCalculator] Rounded ${priceNum} to 5 sig figs: ${rounded5sig}`);
      return this.removeTrailingZeros(rounded5sig.toFixed(Math.max(0, maxDecimalPlaces)));
    }

    // Apply max decimal places constraint
    const actualDecimals = decPart.length;
    if (actualDecimals > maxDecimalPlaces) {
      const rounded = parseFloat(priceNum.toFixed(maxDecimalPlaces));
      debugLog(`[PriceCalculator] Rounded ${priceNum} to ${maxDecimalPlaces} decimals: ${rounded}`);
      return this.removeTrailingZeros(rounded.toFixed(maxDecimalPlaces));
    }

    // Price is already valid
    debugLog(`[PriceCalculator] Price ${priceNum} is valid (${sigFigs} sig figs, ${actualDecimals} decimals)`);
    return this.removeTrailingZeros(priceNum.toFixed(maxDecimalPlaces));
  }

  /**
   * Remove trailing zeros from a price string
   * Required by Hyperliquid API
   */
  private removeTrailingZeros(price: string): string {
    if (!price.includes('.')) return price;
    return price.replace(/\.?0+$/, '');
  }

  /**
   * Round size to valid Hyperliquid precision
   *
   * Rules:
   * - Truncate (not round) to szDecimals decimal places
   * - Remove trailing zeros
   * - Size of "0" is valid (means full position close or attach to parent position)
   *
   * @param size - Size to format (must be non-negative)
   * @param szDecimals - Size decimals for the asset (from metadata)
   * @returns Size truncated to valid precision
   *
   * @example
   * roundToSzDecimals('44.35', 1) // Returns '44.3'
   * roundToSzDecimals('44.39', 1) // Returns '44.3' (truncated, not rounded)
   * roundToSzDecimals('1.2000', 4) // Returns '1.2' (trailing zeros removed)
   */
  roundToSzDecimals(size: number | string, szDecimals: number): string {
    const sizeStr = typeof size === 'string' ? size : size.toString();

    // Validate input format
    if (!/^-?(\d+\.?\d*|\.\d*)$/.test(sizeStr.trim())) {
      throw new Error(`Invalid size format: ${size}`);
    }

    const sizeNum = parseFloat(sizeStr);
    if (isNaN(sizeNum)) {
      throw new Error(`Invalid size: ${size}`);
    }

    // Negative sizes not allowed
    if (sizeNum < 0) {
      throw new Error(`Size must be non-negative: ${size}`);
    }

    // Special case: size of 0 is valid (full position close or attach to parent)
    if (sizeNum === 0) {
      return '0';
    }

    // Truncate to szDecimals places (don't round up/down)
    const truncated = this.truncateToDecimals(sizeNum, szDecimals);

    // Remove trailing zeros
    return this.removeTrailingZeros(truncated);
  }

  /**
   * Truncate number to specified decimal places (no rounding)
   * @private
   */
  private truncateToDecimals(value: number, decimals: number): string {
    // Use regex to truncate without rounding
    const regex = new RegExp(`^-?(?:\\d+)?(?:\\.\\d{0,${decimals}})?`);
    const result = value.toString().match(regex)?.[0];

    if (!result) {
      throw new Error('Invalid number format');
    }

    return result;
  }

  /**
   * Validate if a price string is reasonable for the given symbol
   */
  async validatePrice(symbol: string, price: string): Promise<boolean> {
    try {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum <= 0) {
        return false;
      }

      const currentPrice = await this.getCurrentPrice(symbol);
      const currentPriceNum = parseFloat(currentPrice);

      // Price should be within 50% of current price (reasonable sanity check)
      const ratio = priceNum / currentPriceNum;
      return ratio > 0.5 && ratio < 1.5;
    } catch {
      return false;
    }
  }

  /**
   * Start background price fetching to keep cache warm
   * Fetches prices periodically to eliminate latency on order placement
   *
   * @param intervalMs - Fetch interval in milliseconds (default: 5000ms = 5s)
   */
  startBackgroundFetch(intervalMs: number = 5000): void {
    if (this.refreshInterval) {
      debugLog('[PriceCalculator] Background fetch already running');
      return;
    }

    debugLog(`[PriceCalculator] Starting background price fetch (interval: ${intervalMs}ms)`);

    // Fetch immediately to warm cache
    this.fetchAllPrices().catch((error) => {
      errorLog('[PriceCalculator] Initial price fetch failed:', error);
    });

    // Then fetch periodically
    this.refreshInterval = setInterval(() => {
      this.fetchAllPrices().catch((error) => {
        errorLog('[PriceCalculator] Background price fetch failed:', error);
      });
    }, intervalMs);
  }

  /**
   * Stop background price fetching
   * Call this when shutting down the SDK to cleanup resources
   */
  stopBackgroundFetch(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
      debugLog('[PriceCalculator] Stopped background price fetch');
    }
  }
}
